import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const PersonalInjuryAttorney = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my personal injury attorney business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps personal injury attorney businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for personal injury attorney businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most personal injury attorney businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my personal injury attorney business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like personal injury attorney. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Personal Injury Attorney - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Personal Injury Attorney CRM", url: "/industries/personal-injury-attorney" }
  ];

  const industryData = {
    name: 'Personal Injury Attorney',
    icon: '⚖️',
    title: 'Stop Missing High-Value Injury Cases & Automate Client Intake',
    subtitle: 'Capture injury leads 24/7, pre-qualify cases instantly, and automate client communication throughout the legal process',
    
    // Problem Section - Challenges they face
    challenges: [
      'Missing potential clients who call after hours when accidents happen',
      'Spending 30+ minutes on intake calls only to find cases aren\'t viable',
      'Losing cases to law firms who respond to inquiries faster',
      'No systematic follow-up for cases under evaluation or pending medical reports',
      'Clients constantly calling for case updates when nothing has changed',
      'Settlement negotiations drag on with poor client communication',
      'Completed successful cases but clients never leave reviews or referrals',
      'Competitors dominate Google search results with more reviews and better presence'
    ],
    
    // Solution Section - How we solve these
    solutions: [
      '24/7 case evaluation form captures injury details, accident information, and preliminary case facts even when your office is closed',
      'Automated case pre-qualification questionnaire filters out non-viable cases before wasting attorney time on consultations',
      'Instant acknowledgment SMS with next steps keeps injured claimants engaged while you evaluate their case',
      'Automated case status updates send clients progress notifications (medical records received, demand sent, settlement offer received) without constant phone calls',
      'Document upload portal allows clients to submit medical records, accident photos, and insurance documents securely online',
      'Settlement calculator tool shows potential case value ranges based on injury type, medical costs, and lost wages',
      'Review and referral automation requests testimonials after successful case resolution to build your reputation and generate new referrals'
    ],
    
    // Features Section - What's included in snapshot
    features: [
      '24/7 Case Evaluation Request Form (accident details & injury info)',
      'Automated Case Pre-Qualification Questionnaire',
      'Instant SMS Acknowledgment & Next Steps Communication',
      'Secure Client Document Upload Portal',
      'Automated Case Status Update Notifications (SMS & Email)',
      'Medical Records Request Automation',
      'Settlement Value Calculator (estimate case worth)',
      'Client Communication Portal (secure messaging)',
      'Referral Source Tracking & Management',
      'Google Review & Testimonial Request Automation',
      'Case Pipeline CRM (evaluation → retained → settlement)',
      'Automated Consultation Scheduling (qualified leads only)',
      'Statute of Limitations Deadline Tracking',
      'Co-Counsel Collaboration Portal (for complex cases)'
    ],
    
    // Results Section - Success metrics
    results: [
      '24/7 injury case inquiry capture',
      '78% reduction in unqualified consultations',
      '5.6x more qualified case consultations booked'
    ],
    
    // SEO Section
    metaTitle: 'Personal Injury Law Firm Growth Platform | Never Miss Injury Cases',
    metaDescription: 'Capture injury cases 24/7, automate client intake, pre-qualify cases instantly, and manage client communication throughout litigation.',
    
    // Reviews Section
    reviews: [
      {
        author: 'Robert H.',
        business: 'Personal Injury Law Firm',
        location: 'Manchester',
        rating: 5,
        text: 'The 24/7 case evaluation form captures injury inquiries around the clock. Accidents don\'t happen 9-5, and we were losing 30-40% of cases to firms that responded faster. Now every inquiry gets instant SMS acknowledgment with next steps. Our qualified case volume increased by 340% in the first 6 months. Absolute game-changer.',
        verified: true
      },
      {
        author: 'Jennifer M.',
        business: 'Accident & Injury Solicitors',
        location: 'Birmingham',
        rating: 5,
        text: 'The automated pre-qualification questionnaire filters out non-viable cases before we spend time on consultations. Used to waste 8-10 hours per week on cases we couldn\'t take. Now only talk to qualified claimants with legitimate injury cases. Our case acceptance rate went from 34% to 82%. Saves our attorneys massive amounts of time.',
        verified: true
      },
      {
        author: 'Michael P.',
        business: 'Road Traffic Accident Lawyers',
        location: 'London',
        rating: 5,
        text: 'The automated case status updates are brilliant. Clients get SMS notifications when we receive medical records, send demand letters, or receive settlement offers. Cut "what\'s happening with my case?" calls by 85%. The document upload portal also means clients can submit accident photos and medical reports securely online. Much more efficient.',
        verified: true
      },
      {
        author: 'Susan K.',
        business: 'Personal Injury & Compensation Claims',
        location: 'Leeds',
        rating: 5,
        text: 'The review automation after successful case resolution is fantastic. Our Google reviews went from 18 to 94 in 9 months. New clients constantly mention seeing our reviews online. The referral tracking system also helps us see which past clients are sending us new cases. Best investment we\'ve made in building our injury practice.',
        verified: true
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Personal Injury Attorney UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for personal injury attorney businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for personal injury attorney UK, personal injury attorney business software, personal injury attorney customer management, personal injury attorney lead generation UK"
        url="/industries/personal-injury-attorney"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default PersonalInjuryAttorney;